import { IcPageHeader } from "@ukic/react";

/**
 * In-app "Docs" tab: a short getting-started + capabilities/limits guide. Not a
 * full manual — just enough to orient a new user. Kept curated (not generated)
 * so it reads well; the authoritative spec lives in docs/SPEC.md.
 */
export default function Docs() {
  return (
    <div className="hl-page hl-stack hl-docs">
      <IcPageHeader heading="Documentation" subheading="Getting started with HORIZONLOOK" />

      <div className="hl-panel hl-stack">
        <h2>What this is</h2>
        <p>
          HORIZONLOOK is a fleet of remote software-defined radios (SDRs) on Raspberry
          Pis, managed entirely from this site — no SSH, no per-device logins. Each Pi
          has one or more USB SDRs (RTL-SDR or HackRF One). You pick a device, pick one
          of its SDRs, and give it a task.
        </p>
      </div>

      <div className="hl-panel hl-stack">
        <h2>The three things an SDR can do</h2>
        <p className="hl-muted">
          Each SDR runs <strong>one task at a time</strong>. Tasks on <em>different</em>
          {" "}SDRs run concurrently, so one Pi can watch one band live, decode another,
          and record a third at once.
        </p>
        <dl className="hl-meta">
          <dt>Live</dt>
          <dd>
            Listen/watch a band in your browser (waterfall + audio via OpenWebRX),
            opened in-page. <strong>One SDR can be Live at a time</strong> per device.
            Works on RTL-SDR and HackRF.
          </dd>
          <dt>Capture</dt>
          <dd>
            Record raw IQ to a SigMF file, compressed and offloaded to cloud storage,
            then downloadable from the device page. See <em>Capture options</em> below.
          </dd>
          <dt>Decode</dt>
          <dd>
            Run a background decoder (paging, aircraft, marine, TETRA text, …) whose
            results stream to the searchable <strong>Signals</strong> page.
          </dd>
        </dl>
      </div>

      <div className="hl-panel hl-stack">
        <h2>Getting started</h2>
        <ol className="hl-doc-list">
          <li><strong>Fleet</strong> → click a device that's <code>active</code>.</li>
          <li>On the device page, find the <strong>SDR card</strong> for the dongle you want.</li>
          <li>Choose <strong>Live</strong>, <strong>Capture</strong>, or <strong>Decode</strong>, set the frequency, and press <strong>Start</strong>.</li>
          <li>Decoded records appear under <strong>Signals</strong>; recordings appear in the device's <strong>Captures</strong> list to download.</li>
        </ol>
      </div>

      <div className="hl-panel hl-stack">
        <h2>Decoders</h2>
        <p className="hl-muted">
          Optional decoders are built per device — install them from the device page's
          <strong> Decoder packages</strong> panel (admin). A decoder that can't run on a
          device's SDR is greyed out in the menu.
        </p>
        <table className="hl-doc-table">
          <thead>
            <tr><th>Decoder</th><th>Signal</th><th>SDR</th><th>Notes</th></tr>
          </thead>
          <tbody>
            <tr><td>rtl_433</td><td>ISM sensors (433/868/915)</td><td>RTL · HackRF</td><td>built-in</td></tr>
            <tr><td>POCSAG/FLEX</td><td>Pager messages</td><td>RTL · HackRF*</td><td>single = RTL; multi-channel = RTL or HackRF</td></tr>
            <tr><td>ACARS / VDL2</td><td>Aircraft data link</td><td>RTL · HackRF</td><td>single + multi-channel</td></tr>
            <tr><td>AIS</td><td>Marine (162 MHz)</td><td>RTL · HackRF</td><td>AIS-catcher</td></tr>
            <tr><td>ADS-B</td><td>Aircraft (1090 MHz)</td><td>RTL only</td><td>needs a 1090 MHz antenna</td></tr>
            <tr><td>DMR</td><td>Talkgroup/IDs (not voice)</td><td>RTL only</td><td>metadata only</td></tr>
            <tr><td>TETRA</td><td>Downlink + SDS text</td><td>RTL only</td><td>install pulls in GNU Radio (heavy)</td></tr>
            <tr><td>GSM survey</td><td>Paging volume + cell ID</td><td>RTL · HackRF</td><td>metadata only (no voice/data); GNU Radio gr-gsm</td></tr>
          </tbody>
        </table>
        <p className="hl-muted">
          A decoder shown but not yet built says “needs install-decoders” — install it
          from the Decoder packages panel.
        </p>
      </div>

      <div className="hl-panel hl-stack">
        <h2>Capture options</h2>
        <ul className="hl-doc-list">
          <li><strong>Frequency</strong> — the center to record around.</li>
          <li><strong>Bandwidth</strong> — how wide a slice to record. This is the sample rate: 2.4 MHz captures ~2.4 MHz of spectrum.</li>
          <li><strong>Duration</strong> — seconds. File size ≈ bandwidth × duration × 2 bytes.</li>
          <li><strong>Gain</strong> — leave blank for auto (RTL AGC); HackRF has no AGC, so a value helps.</li>
          <li><strong>HackRF baseband filter</strong> — advanced; leave blank for auto.</li>
        </ul>
        <p className="hl-muted">
          Bit depth is fixed at 8-bit (datatype <code>cu8</code> on RTL, <code>ci8</code> on
          HackRF). To shrink files, lower the bandwidth or duration. Captures that wouldn't
          fit free disk are blocked.
        </p>
      </div>

      <div className="hl-panel hl-stack">
        <h2>SDRs &amp; limits — good to know</h2>
        <ul className="hl-doc-list">
          <li><strong>RTL-SDR</strong>: cheap, great for narrowband VHF/UHF (paging, AIS, airband) and the best choice for ADS-B.</li>
          <li><strong>HackRF One</strong>: wide tuning (1 MHz–6 GHz), needs ≥2 MHz bandwidth and a <strong>powered USB hub</strong> (it browns out otherwise). No AGC — set a gain.</li>
          <li>Antennas matter: a VHF antenna won't hear 1090 MHz ADS-B. Match the antenna to the band.</li>
          <li>Avoid USB extension cables on the SDR — they cause intermittent dropouts.</li>
          <li>A reboot/update no longer loses your decoders — they auto-resume.</li>
        </ul>
      </div>

      <div className="hl-panel hl-stack">
        <h2>Where the data goes</h2>
        <dl className="hl-meta">
          <dt>Signals</dt>
          <dd>Every decoded record (searchable by device, format, text, tail/flight).</dd>
          <dt>Captures</dt>
          <dd>On each device page — download the SigMF IQ files (resumable, from cloud storage).</dd>
        </dl>
      </div>
    </div>
  );
}
